// Single source of truth for "which URL does this session belong at?".
//
// Session delivery has two distinct runtimes — the tutor runtime
// (/sessions/:id/tutor) and the exam runtime (/sessions/:id/exam). The runtime
// is a property of the SESSION (its `mode`), never of the URL the caller
// happened to navigate to: exam delivery must not expose tutor-only support
// artifacts (docs/01_product/session_lifecycle.md:257, 585-590).

export const SESSION_RUNTIME_TUTOR = 'TUTOR';
export const SESSION_RUNTIME_EXAM = 'EXAM';

/** Session payloads use `session_id` (dashboard/list shapes) or `id` (SessionOut). */
export function getSessionId(session) {
  return session?.session_id ?? session?.id ?? '';
}

/** The runtime a session must be delivered in, derived from its mode alone. */
export function getSessionRuntime(session) {
  return String(session?.mode ?? '').trim().toUpperCase() === 'EXAM'
    ? SESSION_RUNTIME_EXAM
    : SESSION_RUNTIME_TUTOR;
}

/** The delivery URL for a runtime. Never routes to /summary — see getSessionPath. */
export function getSessionRuntimePath(sessionId, runtime) {
  return runtime === SESSION_RUNTIME_EXAM
    ? `/sessions/${sessionId}/exam`
    : `/sessions/${sessionId}/tutor`;
}

/**
 * Session statuses that mean "this session is over".
 *
 * `COMPLETED` reached its planned question count; `ABANDONED` was explicitly
 * ended early (docs/01_product/session_lifecycle.md:576-590). Both are ended, so
 * neither may be linked into a delivery runtime the learner can no longer use.
 */
const ENDED_SESSION_STATUSES = new Set(['COMPLETED', 'ABANDONED']);

/**
 * Where a link to this session should point for this viewer.
 *
 *   PARENT, or an ENDED (COMPLETED/ABANDONED) non-exam session -> /summary
 *   EXAM                                                       -> /exam
 *   otherwise                                                  -> /tutor
 *
 * `ABANDONED` used to fall through to /tutor, which linked a learner into a
 * tutor runtime they cannot answer in. It is an ended session and belongs at its
 * summary, exactly like `COMPLETED`.
 *
 * An ABANDONED *exam* session deliberately stays inside the exam runtime rather
 * than routing to /summary: the exam rule is unchanged for every status, because
 * /summary is the tutor summary surface and renders tutor-only support artifacts
 * that exam delivery must not expose (session_lifecycle.md:293). The exam
 * runtime owns the ended-exam experience (it hands off to /exam/results).
 */
export function getSessionPath(role, session) {
  const sessionId = getSessionId(session);
  const runtime = getSessionRuntime(session);
  const isEnded = ENDED_SESSION_STATUSES.has(String(session?.status ?? '').trim().toUpperCase());

  if (role === 'PARENT' || (isEnded && runtime !== SESSION_RUNTIME_EXAM)) {
    return `/sessions/${sessionId}/summary`;
  }

  return getSessionRuntimePath(sessionId, runtime);
}
